"use client";
import React from "react";
import Navbar from "../layout/navbar/Navbar";
import Image from "next/image";
import Link from "next/link";
import SummaryBar from "./SummaryBar";
import PriceCard from "./PriceCard";
import FinanceablePill from "./FinanceablePill";
import Breadcrumb from "@/components/Common/Breadcrumb";
import { ENTREPRENEURSHIP_CATEGORY_SLUG } from "@/lib/constants";
import { HOME_CRUMB, routeCrumb } from "@/lib/routes";
import type { IBreadcrumbItem } from "@/types/breadcrumb";
import type { IFormationDetail } from "@/types/formation";

interface FicheHeroProps {
  formation: IFormationDetail;
}

const FicheHero: React.FC<FicheHeroProps> = ({ formation }) => {
  const objectivesList = formation.specific_objectives.slice(0, 3);

  const isFinanceable = formation.is_financeable;

  /* The trail can't be derived from "/formation/:slug" — the category that owns the
     formation isn't in the URL. Entreprenariat formations hang off their own
     page instead of the catalog (they are excluded from it), so they get a
     shorter trail rather than a crumb that would only redirect. */
  const breadcrumbItems: IBreadcrumbItem[] =
    formation.category_slug === ENTREPRENEURSHIP_CATEGORY_SLUG
      ? [HOME_CRUMB, routeCrumb("/entrepreneurship"), { label: formation.title }]
      : [
          HOME_CRUMB,
          routeCrumb("/formations"),
          {
            label: formation.category,
            href: `/formations/${formation.category_slug}`,
          },
          { label: formation.title },
        ];

  return (
    <section className="relative w-full z-20 lg:z-auto overflow-x-visible">
      {/* Navbar - Refined and aligned */}
      <div className="relative z-100">
        <Navbar />
      </div>

      {/* Background Image */}
      <div className="relative w-full min-h-34 sm:absolute sm:inset-0 sm:h-full z-0">
        <Image
          src="/assets/fiche-hero-bg.svg"
          alt="Image de fond de la fiche"
          title="Image de fond de la fiche"
          aria-hidden="true"
          fill
          priority
          sizes="100vw"
          className="object-cover"
        />

        {/* Mobile title – contributes to height */}
        <div className="sm:hidden px-4 pt-12 relative z-10">
          {/* Mobile copy of the trail — the desktop one lives below, next to the
              title, and is the single emitter of the BreadcrumbList schema. */}
          <Breadcrumb items={breadcrumbItems} withSchema={false} />
          <h1 className="font-red-hat font-normal text-[30px] leading-9.25 text-white tracking-[1.2px]">
            {formation.title}
          </h1>
        </div>
      </div>

      <div className="md:bg-transparent bg-[#1B365E] w-full lg:pt-22 md:pt-17 sm:pt-15 pt-4 md:pb-15.5 pb-8 relative z-10">
        <div className="max-w-(--fiche-container,97.75rem) mx-auto px-4 flex flex-col lg:flex-row justify-between lg:items-start md:gap-12 sm:gap-8 gap-4">
          <div className="fiche-hero-copy sm:order-1 order-2 lg:w-full lg:min-w-0 text-white">
            <Breadcrumb items={breadcrumbItems} className="hidden sm:block" />

            <div className="sm:flex hidden items-center flex-wrap gap-3.5 mt-4">
              <Link
                href={`/formations/${formation.category_slug}`}
                className="bg-white px-2.5 py-1 rounded-full text-[11px] font-bold leading-5 text-[#1B515E] hover:opacity-90 transition-opacity"
              >
                {formation.category}
              </Link>
              {isFinanceable && <FinanceablePill />}
              {formation.reference && (
                <span className="text-[11px] leading-5 text-white/70">
                  {formation.reference}
                </span>
              )}
            </div>

            <h1 className="sm:block hidden font-red-hat font-normal xl:text-[60px] sm:text-[50px] text-[30px] xl:leading-19.75 sm:leading-14 leading-9.25 text-white text-left tracking-[1.2px]">
              {formation.title}
            </h1>

            {objectivesList.length > 0 && (
              <div className="mt-4 sm:mt-6">
                <p className="text-sm font-bold leading-5">Objectifs :</p>
                <ul className="space-y-1.5 mt-2">
                  {objectivesList.map((obj, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2.5 text-sm text-white/90 leading-5"
                    >
                      <span className="mt-2 size-1 rounded-full bg-white shrink-0" />
                      <span>{obj}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Summary row  */}
            <SummaryBar
              formation={formation}
              className="hidden lg:block lg:mt-12"
            />
          </div>

          <Link
            href={`/formations/${formation.category_slug}`}
            className="sm:hidden inline-block bg-white px-2.5 py-1 rounded-full text-[11px] font-bold leading-5 text-[#1B515E] mx-auto hover:opacity-90 transition-opacity"
          >
            {formation.category}
          </Link>
          {/* Price Card */}
          <PriceCard
            formation={formation}
            className="lg:hidden sm:order-2 order-1 w-full"
          />
        </div>
      </div>
    </section>
  );
};

export default FicheHero;
